/**
 * Global Add to Cart Function for Wallace Cotton Theme
 * This script exposes a global addToCart function that third-party developers can use
 * to add products to the cart, with all the linked features such as:
 * - Cart drawer content update
 * - Cart drawer opening
 * - Cart count update
 */

(function() {
  /**
   * Global add to cart function
   * @param {Object|FormData} options - Can be a FormData object or a plain object with product info
   * @param {string|number} options.id - The variant ID (required if not using FormData)
   * @param {number} options.quantity - The quantity to add (defaults to 1 if not provided and not using FormData)
   * @param {Object} options.properties - Optional product properties
   * @param {boolean} options.openCartDrawer - Whether to open the cart drawer after adding (defaults to true)
   * @return {Promise} - Returns a Promise that resolves with the cart data or rejects with an error
   */
  window.wallaceCottonAddToCart = function(options, button = null) {
    // Allow developer to pass in a FormData object directly
    let formData;
    let openCartDrawer = true;

    if (options instanceof FormData) {
      formData = options;
    } else {
      // Create a new FormData object from plain object
      formData = new FormData();
      
      // Check for required variant ID
      if (!options.id) {
        return Promise.reject(new Error('Variant ID is required for adding to cart'));
      }
      
      // Add variant ID
      formData.append('id', options.id);
      
      // Add quantity (default to 1 if not provided)
      formData.append('quantity', options.quantity || 1);
      
      // Add any properties if provided
      if (options.properties) {
        Object.keys(options.properties).forEach(key => {
          formData.append(`properties[${key}]`, options.properties[key]);
        });
      }

      // Check if we should open the cart drawer
      if (options.openCartDrawer === false) {
        openCartDrawer = false;
      }
    }

    // Get the cart-items element
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) {
      return Promise.reject(new Error('Cart items element not found'));
    }

    // Get the cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');

    // Use the internal addToCart method on the cart-items element
    return new Promise((resolve, reject) => {
      // Check if addToCart method exists on cartItems
      if (typeof cartItems.addToCart !== 'function') {
        reject(new Error('cartItems.addToCart function is not available'));
        return;
      }

      // Store the original addToCartError method to restore it later
      const originalAddToCartError = cartItems.addToCartError;
      const originalFetch = window.fetch;

      try {
        // Override fetch to intercept the response
        window.fetch = function(...args) {
          return originalFetch.apply(this, args)
            .then(response => {
              // Clone the response so we can use it in our custom code and pass the original to the theme
              return response.clone().json().then(data => {
                // Store the data to resolve our promise later
                window.wallaceCartResponse = data;
                return response;
              }).catch(() => response); // If it's not JSON, just return the original response
            });
        };

        // Override the error handling to capture errors
        cartItems.addToCartError = function(error, buttonElement) {
          // Call the original error handler
          originalAddToCartError.call(cartItems, error, buttonElement);
          // Reject our promise with the error
          reject(error);
        };

        // Add the product to the cart using the theme's built-in method
        cartItems.addToCart(formData, button);

        // Listen for cart update events
        const handleCartUpdate = () => {
          document.removeEventListener('theme:cart:updated', handleCartUpdate);
          
          // Restore original methods
          window.fetch = originalFetch;
          cartItems.addToCartError = originalAddToCartError;

          // Show the cart drawer if requested
          if (openCartDrawer && cartDrawer) {
            cartDrawer.dispatchEvent(new Event('theme:cart-drawer:show'));
          }

          // Resolve with cart data
          if (window.wallaceCartResponse) {
            const response = window.wallaceCartResponse;
            delete window.wallaceCartResponse;
            resolve(response);
          } else {
            // If we don't have a response, resolve with current cart data
            fetch('/cart.js')
              .then(res => res.json())
              .then(resolve)
              .catch(reject);
          }
        };

        document.addEventListener('theme:cart:updated', handleCartUpdate);

        // Set a timeout to prevent hanging promises
        setTimeout(() => {
          document.removeEventListener('theme:cart:updated', handleCartUpdate);
          window.fetch = originalFetch;
          cartItems.addToCartError = originalAddToCartError;

          // If we have a response already, resolve with it
          if (window.wallaceCartResponse) {
            const response = window.wallaceCartResponse;
            delete window.wallaceCartResponse;
            resolve(response);
          } else {
            // Otherwise, check the cart state
            fetch('/cart.js')
              .then(res => res.json())
              .then(resolve)
              .catch(reject);
          }
        }, 5000);
      } catch (error) {
        // Restore original methods
        window.fetch = originalFetch;
        cartItems.addToCartError = originalAddToCartError;
        reject(error);
      }
    });
  };

  // Add restricted products check for convenience
  window.wallaceCottonHasRestrictedProducts = function() {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) return false;

    // These restricted titles and product IDs should match the ones in addToCartHover.js
    const restrictedTitles = ['Physical Gift Card', 'Digital Gift Card'];
    const restrictedProductIds = ['10024391737653', '10024389214517'];

    const items = cartItems.querySelectorAll('.cart__item');
    for (const item of items) {
      const title = item.getAttribute('data-product-title');
      const productId = item.getAttribute('data-product-id');

      if (restrictedTitles.includes(title) || restrictedProductIds.includes(productId)) {
        return true;
      }
    }
    return false;
  };

  // Add a helper function to check for pre-orders
  window.wallaceCottonIsPreOrder = function(variantId, variantInfo) {
    // We need to check if the checkPreOrder function exists
    if (typeof checkPreOrder === 'function') {
      return checkPreOrder(variantId, [variantInfo]);
    }
    // If the function doesn't exist, we'll check manually
    if (variantInfo) {
      const now = new Date();
      const preOrderFrom = variantInfo.preorder_from ? new Date(variantInfo.preorder_from) : null;
      const preOrderRelease = variantInfo.preorder_release ? new Date(variantInfo.preorder_release) : null;
      
      if (preOrderFrom && now >= preOrderFrom) {
        if (!preOrderRelease || now < preOrderRelease) {
          return true;
        }
      }
    }
    return false;
  };

  // Log that the global add to cart function is available
  console.log('Wallace Cotton Global Add to Cart function is now available as window.wallaceCottonAddToCart');

  // Document the function
  console.log(`
Usage examples:

1. Basic usage:
wallaceCottonAddToCart({ 
  id: 12345678901234, 
  quantity: 1 
})
.then(cart => console.log('Product added, cart:', cart))
.catch(error => console.error('Error adding to cart:', error));

2. With properties:
wallaceCottonAddToCart({
  id: 12345678901234,
  quantity: 2,
  properties: {
    'Pre-order': 'True',
    'Gift': 'Yes'
  }
})
.then(cart => console.log('Product added with properties, cart:', cart))
.catch(error => console.error('Error adding to cart:', error));

3. Without opening cart drawer:
wallaceCottonAddToCart({
  id: 12345678901234,
  quantity: 1,
  openCartDrawer: false
})
.then(cart => console.log('Product added without opening drawer, cart:', cart))
.catch(error => console.error('Error adding to cart:', error));

4. Using FormData directly:
const formData = new FormData();
formData.append('id', 12345678901234);
formData.append('quantity', 1);
formData.append('properties[Custom]', 'Value');

wallaceCottonAddToCart(formData)
.then(cart => console.log('Product added using FormData, cart:', cart))
.catch(error => console.error('Error adding to cart:', error));
`);
})();
